import React from "react";
import { motion } from "framer-motion";
import { useT } from "@/i18n/LanguageContext";

// Cafeo "Three Moods" — a triptych of iced-coffee moods, one per SKU. The two
// canned SKUs (Dark Chocolate Truffle + Dulce de Leche) get their can photo;
// the Dark Americano gets a typography-only pitch-black card (no image asset
// yet) so the mood — bracing, undressed — is expressed in the composition.
// -----------------------------------------------------------------------------
const COCOA = "#2A1B0F";
const GOLD = "#F4C169";
const CREAM = "#F7EBD5";

const MOODS = [
  {
    key: "darkChoc",
    image: "/products/p11.webp",
    bg: "#3E2716",
    ink: "#F7EBD5",
    accent: "#E8998C",
    typography: false,
  },
  {
    key: "dulce",
    image: "/products/p10.webp",
    bg: "#E9D4A9",
    ink: "#3B2410",
    accent: "#B96D5E",
    typography: false,
  },
  {
    key: "americano",
    image: "/products/p12.webp",
    bg: "#1F1610",
    ink: "#F7EBD5",
    accent: "#F4C169",
    typography: false,
  },
];

const FALLBACK = {
  darkChoc: {
    name: "The dark and truffled.",
    flavour: "Dark Chocolate Truffle",
    when: "For late afternoon",
    body: "70% cacao meets a slow-brewed Arabica. Best sipped between the meeting and the walk home.",
  },
  dulce: {
    name: "The warm one.",
    flavour: "Dulce de Leche",
    when: "For after dinner",
    body: "Caramelised milk over cold-brewed espresso. Almost a dessert. Almost.",
  },
  americano: {
    name: "The straight-shot.",
    flavour: "Dark Americano",
    when: "For morning fog",
    body: "No sugar, no milk. Just brewed Arabica, chilled and canned. The clearest way to start the day.",
  },
};

const WHEN_LABELS = {
  darkChoc: { en: "Late afternoon", tr: "Öğleden sonra", fr: "En fin d'après-midi", ru: "Ближе к вечеру", zh: "傍晚", ar: "بعد الظهيرة" },
  dulce: { en: "After dinner", tr: "Yemek sonrası", fr: "Après dîner", ru: "После ужина", zh: "晚餐后", ar: "بعد العشاء" },
  americano: { en: "Morning fog", tr: "Sabah sisi", fr: "Matinée brumeuse", ru: "Утренний туман", zh: "清晨迷雾", ar: "ضباب الصباح" },
};

function MoodCard({ mood, index }) {
  const { t, lang } = useT();
  const isTranslated = (v) => typeof v === "string" && !v.startsWith("brandContent.");
  const fb = FALLBACK[mood.key];
  const nameRaw = t(`brandContent.cafeo.moods.${mood.key}.name`);
  const flavourRaw = t(`brandContent.cafeo.moods.${mood.key}.flavour`);
  const bodyRaw = t(`brandContent.cafeo.moods.${mood.key}.body`);
  const name = isTranslated(nameRaw) ? nameRaw : fb.name;
  const flavour = isTranslated(flavourRaw) ? flavourRaw : fb.flavour;
  const body = isTranslated(bodyRaw) ? bodyRaw : fb.body;
  const when = WHEN_LABELS[mood.key]?.[lang] || WHEN_LABELS[mood.key].en;

  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: index * 0.09 }}
      className="relative overflow-hidden rounded-3xl flex flex-col justify-between min-h-[600px] shadow-[0_20px_60px_-25px_rgba(0,0,0,0.5)]"
      style={{ background: mood.bg, color: mood.ink }}
      data-testid={`cafeo-mood-${mood.key}`}
    >
      {/* Photographic-tile layout (Americano — real can + rainforest bokeh) */}
      {mood.photographic && (
        <div className="relative flex items-end justify-center pt-10 pb-4 px-6 min-h-[380px]">
          <img
            src={mood.image}
            alt={flavour}
            className="h-[380px] w-auto object-contain rounded-2xl shadow-[0_25px_45px_rgba(0,0,0,0.35)]"
          />
        </div>
      )}

      {/* Product-can card layout (transparent PNG pack shots) */}
      {!mood.typography && !mood.photographic && (
        <div className="relative flex items-end justify-center pt-10 pb-4 px-6 min-h-[360px]">
          <img
            src={mood.image}
            alt={flavour}
            className="h-[360px] w-auto object-contain drop-shadow-[0_25px_45px_rgba(0,0,0,0.28)]"
          />
        </div>
      )}

      {/* Typography-only card (legacy — no longer used, kept for future variants) */}
      {mood.typography && (
        <div className="relative flex items-center justify-center pt-14 pb-6 px-6 min-h-[360px]">
          <div className="text-center">
            {/* Steam lines */}
            <div className="flex justify-center gap-3 opacity-40 mb-4">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="inline-block w-[3px] h-8 rounded-full"
                  style={{
                    background: mood.accent,
                    transform: `rotate(${i === 1 ? 0 : i === 0 ? -8 : 8}deg)`,
                  }}
                />
              ))}
            </div>
            <p
              className="font-editorial italic text-6xl md:text-7xl leading-none"
              style={{ color: mood.accent }}
            >
              nero
            </p>
          </div>
        </div>
      )}

      <div className="relative px-7 pb-8 pt-2">
        <span
          className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[0.6rem] font-bold uppercase tracking-[0.28em]"
          style={{ background: mood.accent, color: mood.bg }}
        >
          {when}
        </span>

        <h3
          className="mt-5 font-editorial italic leading-[1.05] text-2xl md:text-3xl"
          style={{ color: mood.ink }}
        >
          {name}
        </h3>

        <p
          className="mt-2 text-[0.65rem] font-bold uppercase"
          style={{ color: mood.accent, letterSpacing: "0.28em" }}
        >
          {flavour}
        </p>

        <div
          className="mt-4 h-px w-14"
          style={{ background: `linear-gradient(90deg, ${mood.accent}, transparent)` }}
        />

        <p
          className="mt-4 text-sm md:text-[15px] leading-[1.6]"
          style={{ color: `${mood.ink}cc` }}
        >
          {body}
        </p>
      </div>
    </motion.article>
  );
}

export default function CafeoMoods() {
  const { t } = useT();
  const isTranslated = (v) => typeof v === "string" && !v.startsWith("brandContent.");
  const overlineRaw = t("brandContent.cafeo.moods.overline");
  const overline = isTranslated(overlineRaw) ? overlineRaw : "The Three Moods";
  const titleRaw = t("brandContent.cafeo.moods.title");
  const title = isTranslated(titleRaw)
    ? titleRaw
    : "Three cans. Three ways to slow down time.";

  return (
    <section
      className="relative overflow-hidden"
      style={{ background: COCOA, color: CREAM }}
      data-testid="cafeo-moods"
    >
      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28">
        <div className="max-w-2xl mb-14">
          <p
            className="text-[0.7rem] md:text-xs font-bold uppercase mb-4"
            style={{ color: GOLD, letterSpacing: "0.36em" }}
          >
            {overline}
          </p>
          <h2
            className="font-editorial italic leading-[1.05] text-[clamp(2.25rem,4.4vw,3.75rem)]"
            style={{ color: CREAM }}
          >
            {title}
          </h2>
          <div
            className="mt-6 h-px w-24"
            style={{ background: `linear-gradient(90deg, ${GOLD}, transparent)` }}
          />
        </div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {MOODS.map((m, i) => (
            <MoodCard key={m.key} mood={m} index={i} />
          ))}
        </div>
      </div>

      <div
        className="h-[3px] w-full"
        style={{
          background: `linear-gradient(90deg, transparent 5%, ${GOLD} 20%, ${GOLD} 80%, transparent 95%)`,
        }}
      />
    </section>
  );
}
